// components/Aria/hooks/useConversation.ts

'use client';

import { useState, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useAria } from '@/context/AriaContext';

interface ConversationState {
  isProcessing: boolean;
  currentContext: string;
}

interface UseConversationReturn {
  isProcessing: boolean;
  currentContext: string;
  sendMessage: (text: string) => Promise<string>;
  clearConversation: () => void;
  updateContext: (context: string) => void;
}

export const useConversation = (apiEndpoint: string): UseConversationReturn => {
  const [state, setState] = useState<ConversationState>({
    isProcessing: false,
    currentContext: '',
  });

  const { state: ariaState } = useAria();
  const conversationId = useRef<string>(uuidv4());
  const ongoingRequest = useRef<boolean>(false); // To prevent duplicate API calls

  const sendMessage = useCallback(async (text: string): Promise<string> => {
    if (ongoingRequest.current) {
      console.warn('Previous request still processing');
      return '';
    }

    try {
      ongoingRequest.current = true;
      setState(prev => ({ ...prev, isProcessing: true }));

      // Get user details from Aria context
      const userDetails = {
        name: ariaState.userDetails?.name || 'Anonymous',
        country: ariaState.userDetails?.country || 'Unknown',
        city: ariaState.userDetails?.city || 'Unknown'
      };

      // Add a timeout to prevent hanging indefinitely
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

      try {
        // Send to API with correct format
        const response = await fetch('/api/chat', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json',
            'x-session-id': conversationId.current
          },
          body: JSON.stringify({
            userMessage: {
              text: text.trim(),
              sender: 'user',
              timestamp: new Date().toISOString(),
              format: 'text'
            },
            metadata: {
              userDetails,
              format: 'text',
              sessionId: conversationId.current,
              platform: 'web',
              timestamp: new Date().toISOString()
            }
          }),
          signal: controller.signal
        });

        console.log('useConversation: Got API response status:', response.status);

        if (!response.ok) {
          const errorData = await response.json().catch(() => null);
          console.error('API Error details:', errorData);
          throw new Error(`API Error: ${response.statusText}`);
        }

        const data = await response.json();
        console.log('useConversation: API response data:', data);

        if (data.success && data.data?.ariaMessage?.text) {
          clearTimeout(timeoutId);
          return data.data.ariaMessage.text;
        }

        throw new Error('Invalid response format from API');
      } catch (error) {
        clearTimeout(timeoutId);
        if (error instanceof Error && error.name === 'AbortError') {
          console.error('Request timed out after 10 seconds');
          throw new Error('Request timed out. Please try again.');
        }
        throw error;
      }
    } catch (error) {
      console.error('Conversation error:', error);
      return "I'm sorry, I encountered an error. Please try again.";

    } finally {
      ongoingRequest.current = false;
      setState(prev => ({ ...prev, isProcessing: false }));
    }
  }, [ariaState.userDetails]); // Added ariaState.userDetails to dependencies

  const clearConversation = useCallback(() => {
    setState({
      isProcessing: false,
      currentContext: '',
    });
    conversationId.current = uuidv4();
    console.log('useConversation: Conversation cleared.');
  }, []);

  const updateContext = useCallback((context: string) => {
    setState((prev) => ({ ...prev, currentContext: context }));
    console.log('useConversation: Context updated to:', context);
  }, []);

  return {
    isProcessing: state.isProcessing,
    currentContext: state.currentContext,
    sendMessage,
    clearConversation,
    updateContext,
  };
};